import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAuditNote, deleteAuditNote, updateAuditNote } from '../api';
import { useTenantData } from '../context/TenantDataContext';
import { useAuth } from '../context/AuthContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { useColumnWidths } from '../hooks/useColumnWidths';
import SearchInput from '../components/SearchInput';
import ConfirmDialog from '../components/ConfirmDialog';
import DecisionTag from '../components/DecisionTag';
import { DECISION_TYPES, DECISION_LABEL, DecisionIcon } from '../components/DecisionTypeRadios';
import DatePicker from '../components/DatePicker';
import OwnerPicker from '../components/OwnerPicker';
import PortalIcon from '../components/PortalIcon';
import NcIcon from '../components/NcIcon';
import DetailEmpty from '../components/DetailEmpty';
import { getStatusBadge } from '../utils/statusBadge';
import { normalizePageKey } from '../utils/legacyPageKeys';
import { findingTitle, workQueuePath, noteSourcePath } from '../utils/findingLabels';
import { noteArea, areaCategoryLabel, AREA_ORDER } from '../utils/noteContext';
import TrailFrame, { TrailTabs, TrailPageLoading } from '../components/TrailFrame';
import { useItList } from '../hooks/useItList';
import { AREA_ICON } from '../data/areaLabels';
import './styles.css';
import './WorkQueueGroups.css';
import './CompliancePage.css';
import './DecisionsRisks.css';

const AREA_OPTIONS = [
  ['identity', 'Brugere'],
  ['devices', 'Enheder'],
  ['security', 'Sikkerhed'],
  ['exchange', 'E-mail'],
  ['sharepoint', 'Grupper'],
  ['applications', 'Entra ID Apps'],
  ['roles', 'Roller'],
  ['conditional-access', 'Loginregler'],
  ['licenses', 'Licenser'],
  ['decisions', 'Andet'],
];

const STATUS_LABEL = { accepted: 'Accepteret risiko', documented: 'Dokumenteret', pending: 'Afventer godkendelse' };
const STATUS_TONE = { accepted: 'warn', documented: 'handled', pending: 'bad' };

// Same four decision types, same order, same icons (DecisionIcon) as Arbejdskø's own inline
// decision form — DECISION_TYPES is the shared source both pages build from, so the two can't drift.
const CHOICE_HINT = {
  accepted: 'Vi kender risikoen og accepterer den bevidst.',
  temp: 'Vi retter det. Angiv, hvem der gør det, og hvornår.',
  compensating: 'En anden foranstaltning dækker risikoen.',
  falsepos: 'Fundet er ikke reelt.',
};

// Areas that have a list in Arbejdskø to link back to.
const WORK_QUEUE_AREAS = ['identity', 'devices', 'security', 'exchange', 'sharepoint', 'applications', 'roles', 'conditional-access', 'licenses'];

const EMPTY_FORM = { title: '', area: 'decisions', text: '', choice: 'accepted', owner: { name: '', email: '' }, reviewDate: '' };

// A note's status is explicit when set by this page's own creation form;
// everything else (the per-object "Acceptér risiko" buttons across the
// app) only ever sets the older boolean `accepted` flag, so that's the
// fallback rather than a second, parallel status field everywhere.
function effectiveStatus(note) {
  if (note.status) return note.status;
  return note.accepted ? 'accepted' : 'documented';
}

// effectiveStatus() above stays coarse on purpose — the filter tabs need
// those three buckets. The label shown on each row is different: it should
// say the real decisionType instead of collapsing everything that isn't an
// accepted risk into "Dokumenteret".
function displayStatus(note) {
  if (note.decisionType) return getStatusBadge(note);
  const status = effectiveStatus(note);
  // Written without choosing a decision type = a note on the object, not a decision.
  if (status === 'documented') return { label: 'Generel note', tone: 'neutral' };
  return { label: STATUS_LABEL[status], tone: STATUS_TONE[status] };
}

function daysUntil(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const due = new Date(y, m - 1, d).getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today.getTime()) / 86400000);
}

function needsReviewSoon(note) {
  const n = daysUntil(note.reviewDate);
  return n !== null && n <= 30;
}


// Which of the four types a note counts as ('' for older free notes and
// the retired "Afventer" status, which only show under "Alle").
function typeOf(note) {
  if (note.decisionType) return note.decisionType;
  return effectiveStatus(note) === 'accepted' ? 'accepted' : '';
}

// Older records store the title as "Bruger: Martin …" — the type is already in
// the line below, so only the name is shown.
function noteTitle(note) {
  return note.title || (note.pageLabel || '').replace(/^(Bruger|Enhed|Applikation|Rolle|Loginregel|Hændelse|SharePoint-site|Licens|Objekt):\s+/, '');
}

function mediumDate(iso) {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

// The four decision types plus "Skal revurderes".
const FILTERS = [
  { key: 'alle', label: 'Alle', predicate: () => true },
  ...DECISION_TYPES.map(([key, label]) => ({ key, label, dot: true, predicate: (n) => typeOf(n) === key })),
  { key: 'review', label: 'Skal revurderes', warn: true, predicate: needsReviewSoon },
];

export default function DecisionsRisks() {
  const navigate = useNavigate();
  // Rows ticked for "Send til support".
  const [picked, setPicked] = useState(() => new Set());
  const { tenant, loading: tenantLoading } = useTenantData();
  const { user, canDecide } = useAuth();
  // Cached (not a bespoke useState+useEffect fetch) so navigating away and
  // back doesn't re-fetch and re-flash the loading state every single time.
  const { data: notesData, refetch: refetchNotes } = useCachedFetch(tenant ? `/api/audit-notes?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const notes = notesData?.notes ?? null;
  const { data: membersData } = useCachedFetch(tenant ? `/api/tenant-members?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  // The signed-in user is always a valid owner, even if the list is empty
  // (e.g. a provider looking at a customer they have no account on).
  const members = useMemo(() => {
    const list = membersData?.members || [];
    if (user?.email && !list.some((m) => m.email === user.email)) {
      return [{ email: user.email, name: user.name || user.email, role: user.role }, ...list];
    }
    return list;
  }, [membersData, user]);
  const [search, setSearch] = useState('');
  const [filterKey, setFilterKey] = useState('alle');
  const [areaKey, setAreaKey] = useState('');
  const [ownerKey, setOwnerKey] = useState('');
  // Same shape as Arbejdskø's own Alle objekter table (tabelSort) — sortable columns instead of
  // always-newest-first, with the exact same header/row markup so the two lists genuinely match.
  const [tabelSort, setTabelSort] = useState({ key: null, dir: 'asc' });
  // Which decision is shown in the side panel — same "list on the left, one persistent panel on the
  // right" workspace Arbejdskø/Konfiguration/Compliance already use, instead of expanding a table row
  // and opening a separate modal to read or edit one. selectedId is set on click; falls back to the
  // first visible row so the panel is never empty while there's something to show (same as the other
  // three pages' own selection fallback).
  const [selectedId, setSelectedId] = useState(null);
  // Opened from the header's own "Send til support" button — same panel picked rows already open,
  // just reachable without ticking anything first, so the capability doesn't stay hidden behind an
  // interaction nobody's told to try.
  const [supportMode, setSupportMode] = useState(false);
  const itList = useItList(tenant?.tenantId);
  // A decision on the IT list: its type and reason follow in the mail, so IT does not have to ask.
  const itItem = (n) => ({ key: `dec:${n.id}`, title: noteTitle(n), area: areaCategoryLabel(noteArea(n)) || '', tone: 'warn', detail: `Beslutning: ${DECISION_LABEL[typeOf(n)] || 'Generel note'}.${n.text ? ` Begrundelse: ${n.text}` : ''}` });
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  // Same resizable detail column as Arbejdskø/Konfiguration/Compliance.
  const detailPanel = useResizablePanel(600, 340, 1000);
  const { flexOf, startResize, reset: resetWidth } = useColumnWidths('nc-tabel-widths', 'decisions');
  const colFlex = (key) => flexOf(key, key === 'object' ? '1.6 1 0%' : '1 1 0%');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Only the areas that actually have a decision are offered in the dropdown.
  const areaOptions = useMemo(() => {
    const present = new Set((notes || []).map((n) => noteArea(n) || '_other'));
    return [...AREA_ORDER, '_other'].filter((k) => present.has(k)).map((k) => [k, k === '_other' ? 'Øvrige' : areaCategoryLabel(k)]);
  }, [notes]);

  // Same idea as areaOptions, one dropdown further — "Alle områder", bare til Ejere.
  const ownerOptions = useMemo(() => {
    const names = new Set((notes || []).map((n) => n.owner).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b, 'da'));
  }, [notes]);

  const inArea = useMemo(
    () => (notes || []).filter((n) => (!areaKey || (noteArea(n) || '_other') === areaKey) && (!ownerKey || n.owner === ownerKey)),
    [notes, areaKey, ownerKey]
  );

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach((f) => { c[f.key] = inArea.filter(f.predicate).length; });
    return c;
  }, [inArea]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filterKey) || FILTERS[0];
    let list = inArea.filter(f.predicate);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (n) =>
          (n.title || '').toLowerCase().includes(q) ||
          (n.text || '').toLowerCase().includes(q) ||
          (n.pageLabel || '').toLowerCase().includes(q) ||
          (n.authorName || '').toLowerCase().includes(q)
      );
    }
    const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
    if (!tabelSort.key) return [...list].sort(byNewest);
    // Same column-sort shape as Arbejdskø's own Alle objekter table — rows with nothing in the
    // sorted column (no owner, no review date) always sort last, whichever direction is picked.
    const text = (v) => String(v || '').toLowerCase();
    const value = {
      object: (n) => text(noteTitle(n)),
      decision: (n) => text(displayStatus(n).label),
      owner: (n) => text(n.owner),
      review: (n) => n.reviewDate || '',
    }[tabelSort.key];
    const dir = tabelSort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      return dir * String(av).localeCompare(String(bv), 'da') || byNewest(a, b);
    });
  }, [inArea, filterKey, search, tabelSort]);

  // Whatever's selected, or the first visible row — the panel is never empty while there's
  // something to show, same fallback Arbejdskø/Konfiguration/Compliance's own lists use.
  const selectedNote = filtered.find((n) => n.id === selectedId) || filtered[0] || null;
  useEffect(() => {
    if (selectedNote && selectedNote.id !== selectedId) setSelectedId(selectedNote.id);
  }, [selectedNote, selectedId]);

  function openNew() {
    setEditingNote(null);
    setForm(EMPTY_FORM);
    setError('');
    setSupportMode(false);
    setFormOpen(true);
  }

  function startEdit(note) {
    setEditingNote(note);
    // note.page can still carry a pre-rename Danish key on notes created
    // before this area-key rename — normalize so the area is recognised.
    setForm({
      title: note.title || '',
      area: normalizePageKey(note.page) || 'decisions',
      text: note.text || '',
      choice: typeOf(note),
      owner: { name: note.owner || '', email: note.ownerEmail || '' },
      reviewDate: note.reviewDate || '',
    });
    setError('');
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingNote(null);
    setForm(EMPTY_FORM);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!tenant) return;
    if (!editingNote && !form.title.trim()) {
      setError('Giv beslutningen en titel');
      return;
    }
    if (!form.choice) {
      setError('Vælg en beslutning');
      return;
    }
    if (!form.text.trim()) {
      setError('Skriv en begrundelse');
      return;
    }
    const decisionType = form.choice;
    const status = form.choice === 'accepted' ? 'accepted' : 'documented';
    setSaving(true);
    setError('');
    try {
      let result;
      if (editingNote) {
        result = await updateAuditNote(tenant.tenantId, editingNote.id, {
          text: form.text.trim(),
          decisionType,
          status,
          reviewDate: form.reviewDate,
          owner: form.owner.name,
          ownerEmail: form.owner.email,
        });
      } else {
        const areaLabel = AREA_OPTIONS.find(([key]) => key === form.area)?.[1] || 'Andet';
        result = await createAuditNote(tenant.tenantId, form.area, areaLabel, form.text.trim(), {
          title: form.title.trim(),
          decisionType,
          status,
          reviewDate: form.reviewDate,
          owner: form.owner.name,
          ownerEmail: form.owner.email,
        });
      }
      if (!result.success) throw new Error(result.error || 'Kunne ikke gemme beslutningen');
      setFormOpen(false);
      setEditingNote(null);
      setForm(EMPTY_FORM);
      await refetchNotes();
      if (result.note?.id) setSelectedId(result.note.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    const result = await deleteAuditNote(tenant.tenantId, deleteTarget.id);
    if (!result.success) throw new Error(result.error || 'Kunne ikke slette beslutningen.');
    setDeleteTarget(null);
    setSelectedId(null);
    await refetchNotes();
  }

  if (tenantLoading || notes === null) return <TrailPageLoading current="decisions" text="Hvad I har valgt at acceptere, og hvorfor. Gennemgå og redigér beslutningerne, og tilføj dem til IT-opgaver, hvis I vil have hjælp." panelWidth={detailPanel.width} />;

  const isMine = (n) => n.authorEmail === user?.email;
  const allPicked = filtered.length > 0 && filtered.every((n) => picked.has(n.id));
  const pickAll = () => setPicked((prev) => { const next = new Set(prev); filtered.forEach((n) => next.add(n.id)); return next; });
  const clearPicked = () => setPicked(new Set());
  const togglePick = (id) => setPicked((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const multiMode = picked.size > 0 || supportMode;

  // Same form fields as before, just rendered inline in the side panel instead of a modal overlay —
  // "we work in the side panel from now on", per direct feedback.
  function FormPanel() {
    return (
      <>
        <div className="detail-head detail-head--compact">
          <div className="detail-head__id">
            <div className="detail-head__text">
              <h3 className="detail-head__title">{editingNote ? 'Rediger beslutning' : 'Generel note'}</h3>
              <div className="detail-head__sub">
                {editingNote
                  ? [noteTitle(editingNote), findingTitle(editingNote.findingKey)].filter(Boolean).join(' · ')
                  : 'Skriv en beslutning, en accepteret risiko eller noget, der skal rettes.'}
              </div>
            </div>
          </div>
        </div>
        <form className="detail-body dc-form-panel" onSubmit={handleSave}>
          {!editingNote && (
            <>
              <label className="dc-field">
                <span>Titel</span>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Fx Aktivér MFA for Shared Kiosk"
                  disabled={saving}
                  autoFocus
                />
              </label>
              <label className="dc-field">
                <span>Område</span>
                <select value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} disabled={saving}>
                  {AREA_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </label>
            </>
          )}
          {/* Same icon-chip treatment (doc-opt/doc-opt__ic) and the same DecisionIcon set Arbejdskø's
              own inline decision form uses — not a lookalike, the identical component. */}
          <div className="doc-opts" role="radiogroup" aria-label="Beslutning">
            {DECISION_TYPES.map(([key, label]) => (
              <button
                type="button"
                key={key}
                role="radio"
                aria-checked={form.choice === key}
                title={CHOICE_HINT[key]}
                className={`doc-opt doc-opt--${key}${form.choice === key ? ' is-selected' : ''}`}
                onClick={() => setForm((f) => ({ ...f, choice: key }))}
                disabled={saving}
              >
                <span className="doc-opt__ic"><DecisionIcon kind={key} /></span>
                <b>{label}</b>
              </button>
            ))}
          </div>
          <label className="dc-field">
            <span>Begrundelse</span>
            <textarea
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              placeholder="Hvad er besluttet, og hvorfor?"
              rows={3}
              disabled={saving}
            />
          </label>
          <div className="dc-two">
            <div className="dc-field">
              <span>Ejer</span>
              <OwnerPicker value={form.owner} members={members} disabled={saving} onChange={(v) => setForm((f) => ({ ...f, owner: v }))} />
            </div>
            <div className="dc-field">
              <span>{form.choice === 'temp' ? 'Frist' : 'Revurderes'}</span>
              <DatePicker value={form.reviewDate} onChange={(v) => setForm((f) => ({ ...f, reviewDate: v }))} placeholder="Vælg dato" ariaLabel={form.choice === 'temp' ? 'Frist for rettelse' : 'Revurderingsdato'} />
            </div>
          </div>
          {error && <p className="dc-error" role="alert">{error}</p>}
          {!canDecide && <p className="dc-error" role="alert">Du har ikke ret til at træffe beslutninger.</p>}
          <div className="dc-modal__foot">
            <button type="button" className="btn-secondary" onClick={closeForm} disabled={saving}>Annullér</button>
            <button type="submit" className="btn-primary" disabled={saving || !canDecide}>{saving ? 'Gemmer…' : editingNote ? 'Gem ændringer' : 'Gem beslutning'}</button>
          </div>
        </form>
      </>
    );
  }

  // Reached either by ticking rows, or straight from the header's "Send til support" button — the
  // panel is the one place this happens now, whether or not anything is ticked yet. Nothing picked
  // shows an explicit empty state (not a blank panel), same as the approved mockup.
  function MultiPanel() {
    const pickedNotes = filtered.filter((n) => picked.has(n.id));
    if (pickedNotes.length === 0) {
      return (
        <>
          <div className="detail-head detail-head--compact">
            <div className="detail-head__id">
              <div className="dc-mcount dc-mcount--empty">0</div>
              <div className="detail-head__text">
                <h3 className="detail-head__title">Tilføj til IT-opgaver</h3>
                <div className="detail-head__sub">Ingen valgt endnu</div>
              </div>
            </div>
            <button type="button" className="dc-modal__x" aria-label="Luk" onClick={() => setSupportMode(false)}><PortalIcon name="x" /></button>
          </div>
          <div className="dc-empty">
            <div className="dc-empty__ic"><PortalIcon name="check" size={18} /></div>
            <p>Sæt kryds ved de beslutninger i listen, I vil have hjælp til — de dukker op her.</p>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="detail-head detail-head--compact">
          <div className="detail-head__id">
            <div className="dc-mcount">{pickedNotes.length}</div>
            <div className="detail-head__text">
              <h3 className="detail-head__title">{pickedNotes.length} {pickedNotes.length === 1 ? 'beslutning' : 'beslutninger'} valgt</h3>
              <div className="detail-head__sub">Tilføj til IT-opgaver</div>
            </div>
          </div>
        </div>
        <div className="detail-body">
          <p className="dc-mnote">Begrundelsen for hver af de valgte følger med i mailen til IT. Intet sendes, før I trykker Send under IT-opgaver.</p>
          <div className="detail-section__label">Valgte</div>
          <div className="dc-mlist">
            {pickedNotes.map((n) => (
              <div className="dc-mrow" key={n.id}>
                <span className="dc-mrow__ic"><NcIcon name={AREA_ICON[noteArea(n)] || 'grid'} size={13} /></span>
                <span className="dc-mrow__txt">
                  <b>{noteTitle(n)}</b>
                  <small>{[areaCategoryLabel(noteArea(n)), DECISION_LABEL[typeOf(n)] || 'Generel note'].filter(Boolean).join(' · ')}</small>
                </span>
                <button type="button" className="dc-mrow__x" aria-label={`Fjern ${noteTitle(n)} fra valget`} onClick={() => togglePick(n.id)}>×</button>
              </div>
            ))}
          </div>
          <div className="dc-acts2">
            <button type="button" className="dc-select__btn" onClick={pickAll} disabled={allPicked}>Vælg alle</button>
            <button type="button" className="dc-select__btn" onClick={clearPicked}>Fravælg alle</button>
          </div>
          <button type="button" className="btn-support dc-support-cta" onClick={() => pickedNotes.forEach((n) => itList.add(itItem(n)).catch(() => {}))}>
            <PortalIcon name="send" size={15} />Tilføj {pickedNotes.length} til IT-opgaver
          </button>
        </div>
      </>
    );
  }

  // The chosen decision, read-only: Begrundelse, then the facts (who owns it, when it was documented, when it is
  // reviewed), and one row of actions. Redigér and Send til support stand side by side; the way on to the object is
  // a quiet link at the end of the row.
  function ViewPanel({ note }) {
    const area = normalizePageKey(note.page);
    const source = noteSourcePath(tenant.tenantId, note);
    const reviewLate = !!(note.reviewDate && needsReviewSoon(note) && daysUntil(note.reviewDate) < 0);
    const reviewSoon = !!(note.reviewDate && needsReviewSoon(note) && !reviewLate);
    return (
      <>
        <div className="detail-head detail-head--compact">
          <div className="detail-head__id">
            <div className="od-avatar" aria-hidden="true"><NcIcon name={AREA_ICON[noteArea(note)] || 'grid'} size={18} /></div>
            <div className="detail-head__text">
              <h3 className="detail-head__title" title={noteTitle(note)}>{noteTitle(note)}</h3>
              <div className="detail-head__sub">{[areaCategoryLabel(noteArea(note)), findingTitle(note.findingKey)].filter(Boolean).join(' · ')}</div>
            </div>
          </div>
          <DecisionTag badge={displayStatus(note)} />
        </div>
        <div className="detail-body dc-body">
          <div className="dc-lab">Begrundelse</div>
          <div className="dc-reason"><p>{`“${note.text}”`}</p></div>

          <div className="dc-lab">Detaljer</div>
          <div className="dc-facts">
            {note.owner && <div className="dc-fact"><span>Ejer</span><span>{note.owner}{!note.ownerEmail && ' (ingen konto)'}</span></div>}
            <div className="dc-fact"><span>Dokumenteret af</span><span>{[note.authorName, mediumDate(note.createdAt)].filter(Boolean).join(' - ')}</span></div>
            {note.editedAt && <div className="dc-fact"><span>Redigeret</span><span>{mediumDate(note.editedAt)}</span></div>}
            {note.reviewDate && (
              <div className={`dc-fact${reviewLate ? ' dc-fact--overdue' : ''}`}>
                <span>{note.decisionType === 'temp' ? 'Frist' : 'Revurderes'}</span>
                <span>
                  {mediumDate(note.reviewDate)}
                  {reviewLate ? (note.decisionType === 'temp' ? ' — fristen er overskredet' : ' — revurdering overskredet') : reviewSoon ? <span className="is-soon"> — snart</span> : ''}
                </span>
              </div>
            )}
          </div>
          <div className="dc-acts3">
            {/* Not gated to the original author: Begrundelse/Ejer/Revurderingsdato are ordinary corrections anyone with
                access to the tenant should be able to make (a colleague fixing a typo, updating an owner who has left). */}
            <button type="button" className="dc-btn dc-btn--ghost" onClick={() => startEdit(note)}>Redigér</button>
            <button type="button" className="dc-btn dc-btn--support" onClick={() => (itList.has(`dec:${note.id}`) ? itList.remove(`dec:${note.id}`) : itList.add(itItem(note))).catch(() => {})}>
              <PortalIcon name="send" size={15} />{itList.has(`dec:${note.id}`) ? 'På IT-opgaver' : 'Tilføj til IT-opgaver'}
            </button>
            <span className="dc-acts3__more">
              {source ? (
                <button type="button" className="dc-link" onClick={() => navigate(source.path)}>{source.label.replace(/\s*→\s*$/, '')}</button>
              ) : WORK_QUEUE_AREAS.includes(area) && (
                <button type="button" className="dc-link" onClick={() => navigate(workQueuePath(tenant.tenantId, area, note))}>Åbn i Gennemgang</button>
              )}
              {isMine(note) && <button type="button" className="dc-del" onClick={() => setDeleteTarget(note)}>Slet</button>}
            </span>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="cmp dc-page">
      <div className="head tf-head">
        <div>
          <div className="eye">Dokumentation</div>
          <h1>Revisionsspor</h1>
          <p>Hvad I har valgt at acceptere, og hvorfor. Gennemgå og redigér beslutningerne, og tilføj dem til IT-opgaver, hvis I vil have hjælp.</p>
        </div>
      </div>

      {notes.length === 0 ? (
        <TrailFrame current="decisions">
        <div className="state-empty">
          <div className="state-empty__icon"><PortalIcon name="scale" size={32} /></div>
          <p>Ingen beslutninger endnu</p>
          <p className="dc-empty-hint">Når I dokumenterer en beslutning i Gennemgang, eller opretter en her, samles de på ét sted.</p>
          <button type="button" className="dc-newnote" style={{ marginTop: 'var(--space-4)' }} onClick={openNew}>Generel note</button>
        </div>
        </TrailFrame>
      ) : (
        <TrailFrame
          current="decisions"
          panelWidth={detailPanel.width}
          tabs={(
            <TrailTabs
              label="Filtrér beslutninger"
              current={filterKey}
              onChange={setFilterKey}
              items={FILTERS.map((f) => ({ key: f.key, label: f.label, count: counts[f.key], warm: !!f.warm, icon: f.dot ? <span className={`dc-tabicon dc-tabicon--${f.key}`}><DecisionIcon kind={f.key} /></span> : null }))}
            />
          )}
          right={(
            <>
              <select className={`dc-area${areaKey ? ' is-on' : ''}`} value={areaKey} onChange={(e) => setAreaKey(e.target.value)} aria-label="Område">
                <option value="">Alle områder</option>
                {areaOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
              <select className={`dc-area${ownerKey ? ' is-on' : ''}`} value={ownerKey} onChange={(e) => setOwnerKey(e.target.value)} aria-label="Ejer">
                <option value="">Alle ejere</option>
                {ownerOptions.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
              <button type="button" className="dc-newnote" onClick={openNew}>Generel note</button>
              <SearchInput value={search} onChange={setSearch} placeholder="Søg..." />
            </>
          )}
        >
        <div className="workspace">
          <div className="list-panel list-panel--float wq-menu">
            <div className="wq-tablecard">
            <div className="list-panel__head">
              <span>{filtered.length} {filtered.length === 1 ? 'beslutning' : 'beslutninger'}</span>
              <span>{tabelSort.key ? 'Sorteret efter kolonnen' : 'Nyeste øverst'}</span>
            </div>
            {/* Real columns (Objekt og fund / Beslutning / Ejer / Revurderes), the same widths on the
                header as on every row below it, instead of a loose flex row where each cell landed
                wherever the previous one's text happened to end. */}
            {/* Same tabel-head-row/tabel-row markup as Arbejdskø's own Alle objekter table — sortable,
                flexibly-widthed columns instead of a fixed grid that truncated "Revurdering
                overskredet" under its own column. */}
            <div className="tabel-head-row">
              <span className="dc-headck" />
              <div className="tabel-head-row__main">
                {[['object', 'Objekt og fund'], ['decision', 'Beslutning'], ['owner', 'Ejer'], ['review', 'Revurderes / frist']].map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    className={`entity-row__sortbtn${tabelSort.key === key ? ' is-active' : ''}`}
                    style={{ flex: colFlex(key) }}
                    onClick={() => setTabelSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))}
                  >
                    {label}
                    {tabelSort.key === key && <span className="entity-row__sortarrow">{tabelSort.dir === 'asc' ? '↑' : '↓'}</span>}
                    <span className="tabel-resize" title="Træk for at ændre bredden. Dobbeltklik nulstiller." onMouseDown={(e) => startResize(e, key)} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => { e.stopPropagation(); resetWidth(key); }} />
                  </button>
                ))}
              </div>
            </div>
            <div className="list-panel__body">
              {filtered.length === 0 && <div className="state-empty"><p>Ingen beslutninger matcher.</p></div>}
              {filtered.map((n) => {
                const left = daysUntil(n.reviewDate);
                const soon = needsReviewSoon(n);
                const isActive = !multiMode && selectedNote?.id === n.id;
                return (
                  <div key={n.id} className={`tabel-row${isActive || picked.has(n.id) ? ' is-selected' : ''}`} onClick={() => { setSupportMode(false); setSelectedId(n.id); }}>
                    <label className="wg__pick" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={picked.has(n.id)} onChange={() => togglePick(n.id)} aria-label={`Vælg ${noteTitle(n)}`} />
                      <span className="wg__cb" aria-hidden="true"><PortalIcon name="check" size={11} /></span>
                    </label>
                    <div className="tabel-row__main">
                      <div className="tabel-row__col" style={{ flex: colFlex('object') }} title={noteTitle(n)}>
                        <b>{noteTitle(n)}</b>
                        <small>{[areaCategoryLabel(noteArea(n)), findingTitle(n.findingKey)].filter(Boolean).join(' · ')}</small>
                      </div>
                      <div className="tabel-row__col" style={{ flex: colFlex('decision') }}><DecisionTag badge={displayStatus(n)} /></div>
                      <div className="tabel-row__col" style={{ flex: colFlex('owner') }} title={n.owner || ''}><b>{n.owner || '—'}</b></div>
                      <div className="tabel-row__col" style={{ flex: colFlex('review') }}>
                        <b>{n.reviewDate ? mediumDate(n.reviewDate) : '—'}</b>
                        {soon && <em className={`dc-soon2${left < 0 ? ' dc-soon2--over' : ''}`}>{left < 0 ? 'Overskredet' : left === 0 ? 'I dag' : `Om ${left} dage`}</em>}
                      </div>
                    </div>
                    <svg className="dc-row__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                  </div>
                );
              })}
            </div>
            </div>
          </div>

          <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />

          <div className="detail-panel-col" style={{ width: detailPanel.width }}>
            <div className="detail-panel">
              {multiMode
                ? MultiPanel()
                : formOpen
                  ? FormPanel()
                  : selectedNote
                    ? ViewPanel({ note: selectedNote })
                    : <DetailEmpty text="Vælg en beslutning i listen for at se detaljer." />}
            </div>
          </div>
        </div>
        </TrailFrame>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Slet beslutning?"
          message={`"${noteTitle(deleteTarget)}" fjernes fra revisionssporet. Rapporter, der allerede er sendt, ændres ikke. Kan ikke fortrydes.`}
          confirmLabel="Slet beslutning"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
